import { useCallback, useEffect, useRef } from "react";
import type { MouseEvent, ReactNode } from "react";

import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";

import { Content } from "./Content";

export interface TagProps {
  autoScrollIntoView?: boolean;
  children?: ReactNode;
  className?: string;
  icon?: ReactNode;
  label?: string;
  onClose?: (event: MouseEvent<HTMLButtonElement>) => void;
  variant?: "primary" | "secondary";
}

export const Tag = ({
  children,
  className = "member-badge",
  onClose,
  label,
  icon = null,
  autoScrollIntoView = false,
  variant = "primary",
}: TagProps) => {
  // I don't think this logic should be at this level
  const divRef = useRef<HTMLDivElement>(null);
  const scrollToDivRef = useCallback(() => {
    setTimeout(
      function () {
        divRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      }.bind(this),
      10,
    );
  }, [divRef]);

  useEffect(() => {
    if (autoScrollIntoView) {
      scrollToDivRef();
    }
  }, [autoScrollIntoView, scrollToDivRef]);

  const computedClassNames = classNames(
    "flex items-center whitespace-nowrap rounded-xl p-1 text-sm font-medium",
    {
      "bg-tertiary": variant === "primary",
      "bg-white": variant === "secondary",
    },
    className,
  );

  return (
    <div ref={divRef} className={computedClassNames}>
      {children || (
        <Content
          label={label}
          onClose={onClose}
          icon={icon}
          closeIconAriaLabel={`${t("remove")} ${label}`}
        />
      )}
    </div>
  );
};
